import Layout from "@/components/elements/Layout";
import ShareButtons from "@/components/parts/ShareButtons";
import ShareButtonsVertical from "@/components/parts/ShareButtonsVertical";
import RelatedContentSidebar from "@/components/RelatedContentSidebar";
import Schema from "@/components/schema/Schema";
import { Button } from "@/components/ui/button";
import {
  useGetContentsByContentType,
  useGetCurrentUrl,
  useGetRelatedContents,
} from "@/hooks";
import useAuth from "@/hooks/auth";
import {
  getContentDetailBySlug,
  getPinnedByContentId,
  trackContentAccessCounting,
} from "@/services/api";
import { cleanHTML, CONTENT_TYPES } from "@/utils/content";
import { formatDate } from "@/utils/datetime";
import isPersonName from "@/utils/isPersonName";
import {
  ArrowLeft,
  BookOpen,
  CaretRight,
  LinkedinLogo,
  TwitterLogo,
  FacebookLogo,
  InstagramLogo,
} from "@phosphor-icons/react";
import Head from "next/head";
import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import ImageLogo from "../../assets/images/nav-logo-vertical.svg";
import { fitIn } from "@/utils/imageHandler";
import SubNavigation from "@/components/elements/SubNavigation";
import Subscribe from "@/components/Subscribe";
import { useAtom } from "jotai";
import { userAtom } from "@/Atoms";
import MentionedVCAndStartup from "@/components/MentionedVCAndStartup";
import ContentWishlistComponent from "@/components/parts/ContentWishlistComponent";
import AuthorContent from "@/components/AuthorContent";

function NewsDetail(props) {
  const router = useRouter();
  const { user } = useAuth();
  const [reach_limit, setReachLimit] = useState(false);
  const [userState, setUserState] = useAtom(userAtom);
  const [expandedAuthors, setExpandedAuthors] = useState({});

  const [related_items, isLoading] = useGetRelatedContents(
    CONTENT_TYPES.NEWS,
    props.article.uuid
  );

  const [article, setArticle] = useState(props.article ?? {});
  // const [otherReports, otherReportLoading] = useGetContentsByContentType(
  //   CONTENT_TYPES.SPECIAL_REPORTS
  // );

  const [pinneds, setPinneds] = useState({
    startups: [],
    ventureCapitals: [],
  });

  const loadArticle = async () => {
    try {
      const response = await getContentDetailBySlug(router.query.slug);
      setArticle(response.data.data);
    } catch {
      // TODO Handle error
    }
  };

  const fetchMeteredContent = async () => {
    if (userState?.roles === "admin-team" || userState?.roles === "read-access") {
      setReachLimit(false);
    } else {
      try {
        const response = await trackContentAccessCounting(router.query.slug, {
          ip_address: localStorage.getItem("lctd-ia"),
          user_id: userState?.id,
          user_email: userState?.email,
        });
        setReachLimit(response.data.reach_limit);
      } catch (error) {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    loadArticle();
    fetchMeteredContent();
    getPinned();
  }, [router.query.slug, userState, article.id]);

  const fullUrl = useGetCurrentUrl();

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      mainEntityOfPage: {
        "@type": "WebPage",
        id: fullUrl,
      },
      headline: article.meta_title,
      image: [article.thumbnail_url],
      datePublished: article.publish_date,
      dateModified: article.publish_date,
      author: {
        "@type": isPersonName(article.author.name) ? "Person" : "Organization",
        name: isPersonName(article.author.name)
          ? article.author.name
          : "Lucidity Insights",
      },
      publisher: {
        "@type": "Organization",
        name: "Lucidity Insights",
        logo: {
          "@type": "ImageObject",
          url: ImageLogo.src,
        },
      },
      description: article.meta_desc,
    },
  ];

  // const structuredData = [
  //   {
  //     "@context": "https://schema.org",
  //     "@type": "Article",
  //     mainEntityOfPage: {
  //       "@type": "WebPage",
  //       "@id": fullUrl,
  //     },
  //     headline: article.meta_title,
  //     description: article.meta_desc,
  //     image: article.thumbnail_url,
  //     //  author: {
  //     //    "@type": "Person",
  //     //    name: "Erica",
  //     //    url: "https://lucidityinsights.com/author/erica",
  //     //  },
  //     publisher: {
  //       "@type": "Organization",
  //       name: "Lucidity Insights",
  //       logo: {
  //         "@type": "ImageObject",
  //         url: ImageLogo.src,
  //       },
  //     },
  //     datePublished: article.publish_date,
  //     dateModified: article.publish_date,
  //   },
  //   {
  //     "@context": "https://schema.org/",
  //     "@type": "BreadcrumbList",
  //     itemListElement: [
  //       {
  //         "@type": "ListItem",
  //         position: 1,
  //         name: "Home",
  //         item: `${process.env.BASE_URL}`,
  //       },
  //       {
  //         "@type": "ListItem",
  //         position: 2,
  //         name: "Article",
  //         item: `${process.env.BASE_URL}articles`,
  //       },
  //       {
  //         "@type": "ListItem",
  //         position: 3,
  //         name: article.title,
  //         item: `${process.env.BASE_URL}articles/${router.query.slug}`,
  //       },
  //     ],
  //   },
  // ];

  const getPinned = async () => {
    setPinneds({
      startups: [],
      ventureCapitals: [],
    });
    try {
      const res = await getPinnedByContentId(article.id);
      setPinneds(res.data.data);
    } catch (error) {
      setPinneds({
        startups: [],
        ventureCapitals: [],
      });
    }
  };

  const transformYoutubeLinks = (content) => {
    const youtubeRegex =
      /@(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)(?:&\S+)?/g;

    return content.replace(youtubeRegex, (match, videoId) => {
      const iframe = `
        <div class="relative aspect-video w-full my-6">
          <iframe
            class="absolute top-0 left-0 w-full h-full rounded-lg"
            src="https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&controls=1&showinfo=0"
            title="YouTube video player"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>
      `;
      return iframe;
    });
  };

  const toggleAuthorDescription = (authorId) => {
    setExpandedAuthors((prev) => ({
      ...prev,
      [authorId]: !prev[authorId],
    }));
  };

  return (
    <div>
      <Head>
        <meta
          property='article:published_time'
          content={new Date(article.publish_date).toISOString()}
        />
        <meta
          property='article:modified_time'
          content={new Date(article.publish_date).toISOString()}
        />
        <meta property='article:section' content='Article Section' />
      </Head>
      <Schema data={structuredData} />
      <Layout
        title={article.title}
        meta_title={article.meta_title}
        description={article.meta_desc}
        meta_image={article.cover_image_url}
      >
        <SubNavigation title={article.title}></SubNavigation>
        <div className='px-4 mx-auto max-w-7xl lg:flex'>
          <div className='flex items-start flex-1 pt-6 pb-10 lg:pt-10 lg:pb-16 lg:pr-8 lg:mr-8 lg:border-r lg:space-x-8 2xl:space-x-12 border-white/5'>
            <div className='sticky hidden pt-2 lg:block top-40'>
              <ShareButtonsVertical
                title={article.title}
                description={article.meta_desc}
                url={fullUrl}
              />
            </div>
            <div className='relative w-full space-y-10 lg:space-y-16 max-w-screen-2xl'>
              <article className='relative mx-auto'>
                <h1 className='text-3xl leading-[1.1] font-bold tracking-tight lg:text-5xl font-display'>
                  {article.title}
                </h1>
                <div className='flex items-center justify-between gap-4 mt-3'>
                  <p className='flex items-center text-sm'>
                    <span className='opacity-40'>
                      {`${formatDate(article.publish_date, "DD MMMM YYYY")}`}
                    </span>
                    <span className='ml-2 opacity-40'>&bull;</span>
                    <ContentWishlistComponent item={article} />
                  </p>
                  <span className='flex-1 h-px bg-white/10'></span>
                  <div className='lg:hidden'>
                    <ShareButtons
                      title={article.title}
                      description={article.meta_desc}
                      url={fullUrl}
                    />
                  </div>
                </div>

                <div className='pt-3 pb-6 text-sm text-white/70'>
                  By{" "}
                  {article.authors.map((author, index) => (
                    <Link
                      key={index}
                      href={`/author/${author.slug}`}
                      className='no-underline'
                    >
                      <span className='underline'>{author.name}</span>
                      {article.authors.length > 2 &&
                        index < article.authors.length - 2 &&
                        ", "}
                      {article.authors.length > 1 &&
                        index === article.authors.length - 2 &&
                        " and "}
                    </Link>
                  ))}{" "}
                </div>

                <div className='relative w-full h-auto'>
                  <Image
                    src={fitIn(article.cover_image_url, 1200)}
                    alt={article.cover_image_alt}
                    width={640}
                    height={320}
                    layout={"responsive"}
                    objectFit={"cover"}
                    className='rounded-[15px]'
                  />
                </div>
                <div className='pt-6 mx-auto prose lg:pt-8 content prose-invert prose-img:rounded-lg'>
                  {cleanHTML(transformYoutubeLinks(article.description))}
                </div>
              </article>
              <MentionedVCAndStartup
                pinneds={pinneds}
                locked={reach_limit}
                isLogin={user?.id}
              />
              <AuthorContent data={article.authors} />
            </div>
          </div>

          <div className='lg:pt-10 lg:max-w-[340px] text-white pb-10 lg:pb-16 space-y-8 lg:space-y-10'>
            {article.connected_with && (
              <div>
                <h2 className='pb-2 mb-4 text-lg font-semibold border-b border-white/10'>
                  Related Report
                </h2>
                <Link
                  href={`/reports/${article.connected_with.slug}`}
                  className='relative flex items-start space-x-6 group'
                >
                  <div className='flex-1'>
                    <p className='pt-1 mb-2 text-lg font-semibold leading-tight tracking-tight text-white group-hover:underline lg:leading-tight'>
                      {article.connected_with.title}
                    </p>
                    <p className='mb-3 text-xs font-medium text-white/40 line-clamp-3'>
                      {article.connected_with.excerpt}
                    </p>
                    {/* <Button
                      onClick={() =>
                        router.push(`/reports/${article.connected_with.slug}`)
                      }
                      className='w-full text-white bg-background hover:bg-zinc-900'
                    >
                      <BookOpen weight='bold' className='mr-2.5' />
                      <span>Read Full Report</span>
                    </Button> */}
                  </div>
                  <div className='w-20'>
                    <div className='relative aspect-[3/4] rounded overflow-hidden border border-white/10'>
                      <Image
                        src={fitIn(article.connected_with.cover_image_url, 600)}
                        layout='fill'
                        objectFit='cover'
                        alt={article.connected_with.cover_image_alt}
                      />
                    </div>
                  </div>
                </Link>
              </div>
            )}
            <RelatedContentSidebar
              title='Related Videos & Podcasts'
              currentContentId={article.uuid}
              contentType={CONTENT_TYPES.VIDEOS_AND_PODCAST_EPISODES}
            />
            <RelatedContentSidebar
              title='Did you check these out?'
              currentContentId={article.uuid}
            />
          </div>
        </div>
        <Subscribe />

        {/* <div className='mb-14 lg:mb-24'>
          {otherReports.length > 0 && (
            <SpecialReportsSlider
              reports={otherReports.filter(
                (other) => other.uuid !== article.connected_with.uuid
              )}
              isLoading={otherReportLoading}
              title='Check Out Some of Our Other Reports'
              decoration
            />
          )}
        </div> */}
      </Layout>
    </div>
  );
}

export async function getServerSideProps({ query }) {
  try {
    const response = await getContentDetailBySlug(query.slug, {
      params: {
        contentTypeName: CONTENT_TYPES.NEWS,
      },
    });
    return {
      props: {
        article: response.data.data,
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
}

export default NewsDetail;
